import { useEffect, useState } from "react";
import { ImagePlus, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../../lib/api";
import { useConfirm } from "../../components/Confirm";

const empty = { title: "", description: "", image: "", date_label: "", journal: "", order: 0 };

const readImage = (file) => new Promise((resolve, reject) => {
  if (file.size > 2 * 1024 * 1024) return reject(new Error("حجم الصورة يتجاوز 2MB"));
  const img = new Image(); const url = URL.createObjectURL(file);
  img.onload = () => {
    const max = 1400; const scale = Math.min(1, max / Math.max(img.width, img.height));
    const canvas = document.createElement("canvas"); canvas.width = img.width * scale; canvas.height = img.height * scale;
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url); resolve(canvas.toDataURL("image/jpeg", 0.82));
  };
  img.onerror = reject; img.src = url;
});

const AchievementForm = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState(initial || empty);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const onFile = async (e) => { const f = e.target.files?.[0]; if (!f) return; try { setForm({ ...form, image: await readImage(f) }); } catch (err) { toast.error(err.message || "تعذر قراءة الصورة"); } };

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    const payload = { ...form, order: Number(form.order) || 0 }; delete payload.id; delete payload.created_at; delete payload.updated_at;
    try { initial ? await api.put(`/admin/achievements/${initial.id}`, payload) : await api.post("/admin/achievements", payload); toast.success("تم الحفظ"); onSaved(); }
    catch (err) { toast.error(errorMessage(err, "تعذر الحفظ")); } finally { setLoading(false); }
  };

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" data-testid="achievement-form-modal">
        <button className="modal-close" onClick={onClose} data-testid="achievement-form-close"><X size={18} /></button>
        <h2>{initial ? "تعديل الإنجاز" : "إنجاز جديد"}</h2>
        <form className="form" onSubmit={submit}>
          <label className="full">العنوان<input value={form.title} onChange={set("title")} required data-testid="ach-title" /></label>
          <label className="full">الوصف<textarea value={form.description} onChange={set("description")} data-testid="ach-description" /></label>
          <label>المجلة / التصنيف<input value={form.journal} onChange={set("journal")} placeholder="Q1 · Scopus" data-testid="ach-journal" /></label>
          <label>التاريخ (نص)<input value={form.date_label} onChange={set("date_label")} placeholder="يناير 2026" data-testid="ach-date" /></label>
          <label>الترتيب<input type="number" value={form.order} onChange={set("order")} data-testid="ach-order" /></label>
          <label>رابط صورة (اختياري)<input value={form.image.startsWith("data:") ? "" : form.image} onChange={set("image")} placeholder="https://..." data-testid="ach-image-url" /></label>
          <label className="full upload-box" style={{ display: "block" }}>
            {form.image && <img src={form.image} alt="" />}
            <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}><ImagePlus size={16} />{form.image ? "تغيير الصورة" : "رفع صورة من الجهاز (حتى 2MB)"}</span>
            <input type="file" accept="image/*" onChange={onFile} style={{ display: "none" }} data-testid="ach-image-file" />
          </label>
          <button className="btn btn-primary" type="submit" disabled={loading} data-testid="ach-save-button">{loading ? <span className="loader" /> : "حفظ"}</button>
        </form>
      </div>
    </div>
  );
};

export default function AdminAchievements() {
  const confirm = useConfirm();
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(undefined);
  const load = () => api.get("/achievements").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (a) => { if (!(await confirm(`حذف الإنجاز "${a.title}" نهائياً؟`, { danger: true, okLabel: "حذف" }))) return; try { await api.delete(`/admin/achievements/${a.id}`); toast.success("تم الحذف"); load(); } catch (err) { toast.error(errorMessage(err, "تعذر الحذف")); } };

  return (
    <div data-testid="admin-achievements">
      <div className="admin-top"><div><span className="kicker">Achievements</span><h1>إنجازات المنصة</h1></div><button className="btn btn-primary btn-sm" onClick={() => setEditing(null)} data-testid="add-achievement-button"><Plus size={16} />إنجاز جديد</button></div>
      {items === null ? <div className="page-loader"><span className="loader" /></div> : items.length === 0 ? <div className="empty">لا توجد إنجازات بعد — أضف أول إنجاز</div> : (
        <div className="ach-grid">{items.map((a) => (
          <div className="ach-card" key={a.id} data-testid={`admin-ach-${a.id}`}>
            {a.image && <div className="ach-img"><img src={a.image} alt={a.title} /></div>}
            <div className="ach-body"><h3>{a.title}</h3>{a.description && <p style={{ display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{a.description}</p>}
              <div className="row-actions" style={{ marginTop: 12 }}><button className="mini-btn" onClick={() => setEditing(a)} data-testid={`edit-ach-${a.id}`}><Pencil size={14} />تعديل</button><button className="mini-btn danger" onClick={() => remove(a)} data-testid={`delete-ach-${a.id}`}><Trash2 size={14} /></button></div></div>
          </div>))}</div>
      )}
      {editing !== undefined && <AchievementForm initial={editing} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />}
    </div>
  );
}
